import { Logger } from "@/utils/logger";

const STATSIG_CDN_URL = "https://api.statsigcdn.com/v1/download_config_specs";

interface ConfigRule {
  returnValue: Record<string, unknown>;
  passPercentage: number;
}

interface ConfigSpec {
  name: string;
  defaultValue: Record<string, unknown>;
  enabled: boolean;
  rules: ConfigRule[];
}

export interface ConfigSpecsResponse {
  dynamic_configs: ConfigSpec[];
}

export async function fetchConfigSpecs(): Promise<ConfigSpecsResponse | null> {
  // CDN config specs endpoint requires a server secret key
  const key = process.env.STATSIG_SERVER_KEY;
  if (!key) return null;

  try {
    const res = await fetch(`${STATSIG_CDN_URL}?k=${encodeURIComponent(key)}`, {
      next: { revalidate: 30 },
    });
    if (!res.ok) {
      Logger.error(new Error("[statsig] config specs fetch failed"), {
        extra: { status: res.status },
      });
      return null;
    }
    return (await res.json()) as ConfigSpecsResponse;
  } catch (error) {
    Logger.error(error, { extra: { source: "[statsig] config specs fetch" } });
    return null;
  }
}

/**
 * Extract the value for a dynamic config from raw config specs.
 * Uses the last rule (the default "Everyone" rule) since we evaluate for an anonymous user.
 */
export function getDynamicConfigValue(
  specs: ConfigSpecsResponse,
  configName: string,
): Record<string, unknown> {
  const config = specs.dynamic_configs.find((c) => c.name === configName);

  if (!config?.enabled) return {};

  return config.defaultValue;
}
